import { debounce } from '@theme/utilities';

/**
 * `<masonry-grid>` — positions children using shortest-column-first masonry.
 *
 * When the sole meaningful child is a `<ul>` or `<ol>`, masonry is applied to
 * that list's `<li>` items (the list becomes the positioning container). In all
 * other cases masonry is applied to the element's own direct children.
 *
 * @attr {number} data-gap-h           Horizontal gap in px (default: 16)
 * @attr {number} data-gap-v           Vertical gap in px   (default: 16)
 * @attr {number} data-columns-desktop Columns at ≥990px    (default: 4)
 * @attr {number} data-columns-tablet  Columns at 750–989px (default: 3)
 * @attr {number} data-columns-mobile  Columns at <750px    (default: 2)
 *
 * @example Standalone — items are direct children:
 *   <masonry-grid data-gap-h="16" data-gap-v="16" data-columns-desktop="3">
 *     <div class="card">…</div>
 *     <div class="card">…</div>
 *   </masonry-grid>
 *
 * @example List wrapper — masonry applied to <li> items:
 *   <masonry-grid data-gap-h="16" data-gap-v="16">
 *     <ul role="list">
 *       <li>…</li>
 *     </ul>
 *   </masonry-grid>
 */
class MasonryGrid extends HTMLElement {
  /** @type {number} */
  #lastWidth = 0;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {MutationObserver | undefined} */
  #mutationObserver;
  /** @type {Function & { cancel(): void }} */
  #scheduleLayout;
  /** @type {Function & { cancel(): void }} */
  #scheduleLayoutWithImages;

  get #gapH() {
    return parseInt(this.dataset.gapH ?? '16', 10);
  }

  get #gapV() {
    return parseInt(this.dataset.gapV ?? '16', 10);
  }

  /** @returns {number} */
  get #columnCount() {
    const desktop = parseInt(this.dataset.columnsDesktop ?? '4', 10);
    const tablet = parseInt(this.dataset.columnsTablet ?? '3', 10);
    const mobile = parseInt(this.dataset.columnsMobile ?? '2', 10);
    if (window.innerWidth >= 990) return desktop;
    if (window.innerWidth >= 750) return tablet;
    return mobile;
  }

  /**
   * Element that receives `position: relative` and an explicit height.
   * If the first child is a list, that list is the container so its <li>
   * items are the masonry items and the list's own dimensions are managed.
   * @returns {HTMLElement}
   */
  get #container() {
    const first = /** @type {HTMLElement | null} */ (this.firstElementChild);
    return first?.tagName === 'UL' || first?.tagName === 'OL' ? first : this;
  }

  /**
   * Elements to position. For list containers only <li> children are included
   * (skipping any inline <script> tags). For generic containers <script>,
   * <style>, and <template> nodes are excluded.
   * @returns {HTMLElement[]}
   */
  get #items() {
    const container = this.#container;
    if (container.tagName === 'UL' || container.tagName === 'OL') {
      return /** @type {HTMLElement[]} */ ([...container.children].filter(
        (el) => el.tagName === 'LI'
      ));
    }
    const excluded = new Set(['SCRIPT', 'STYLE', 'TEMPLATE']);
    return /** @type {HTMLElement[]} */ ([...container.children].filter(
      (el) => !excluded.has(el.tagName)
    ));
  }

  connectedCallback() {
    // Cancel any timers left over from a previous connection
    this.#scheduleLayout?.cancel();
    this.#scheduleLayoutWithImages?.cancel();

    // Resize path: images already loaded, go straight to layout
    this.#scheduleLayout = /** @type {any} */ (debounce(() => this.#layout(), 50));
    // Mutation path: new items (e.g. infinite scroll) may have unloaded images
    this.#scheduleLayoutWithImages = /** @type {any} */ (
      debounce(() => this.#waitForImages().then(() => this.#layout()), 50)
    );

    // Immediate pass — Shopify images carry width/height so heights are
    // estimated from aspect ratio even before bytes arrive.
    this.#layout();
    // Second pass once images are fully loaded to correct any estimates.
    this.#waitForImages().then(() => this.#layout());

    this.#resizeObserver = new ResizeObserver((entries) => {
      // Skip entries where only height changed — that's us setting container
      // height, not a real resize. Prevents the ResizeObserver feedback loop.
      const width =
        entries[0]?.contentBoxSize?.[0]?.inlineSize ?? entries[0]?.contentRect.width ?? 0;
      if (Math.round(width) === Math.round(this.#lastWidth)) return;
      this.#scheduleLayout();
    });
    this.#resizeObserver.observe(this.#container);

    this.#mutationObserver = new MutationObserver(this.#scheduleLayoutWithImages);
    this.#mutationObserver.observe(this.#container, { childList: true });
  }

  disconnectedCallback() {
    this.#scheduleLayout?.cancel();
    this.#scheduleLayoutWithImages?.cancel();
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
  }

  /** @returns {Promise<void>} */
  async #waitForImages() {
    const imgs = /** @type {HTMLImageElement[]} */ (
      [...this.querySelectorAll('img')].filter(
        (img) => !/** @type {HTMLImageElement} */ (img).complete
      )
    );
    if (!imgs.length) return;
    await Promise.all(
      imgs.map(
        (img) =>
          new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          })
      )
    );
  }

  #layout() {
    const container = this.#container;
    const items = this.#items;
    if (!items.length) return;

    const cols = this.#columnCount;
    const gapH = this.#gapH;
    const gapV = this.#gapV;
    const containerWidth = container.offsetWidth;
    if (!containerWidth) return;

    this.#lastWidth = containerWidth;
    const itemWidth = (containerWidth - gapH * (cols - 1)) / cols;

    // Write: set container display/position and item widths
    container.style.display = 'block';
    container.style.position = 'relative';

    for (const item of items) {
      item.style.position = 'absolute';
      item.style.width = `${itemWidth}px`;
    }

    // Read: measure all heights in one batch (single reflow)
    const heights = items.map((item) => item.offsetHeight);

    // Write: assign positions — physical properties are intentional (masonry is LTR by spec)
    const colHeights = new Array(cols).fill(0);
    for (let i = 0; i < items.length; i++) {
      const col = colHeights.indexOf(Math.min(...colHeights));
      items[i].style.left = `${col * (itemWidth + gapH)}px`;
      items[i].style.top = `${colHeights[col]}px`;
      colHeights[col] += heights[i] + gapV;
    }

    container.style.height = `${Math.max(...colHeights) - gapV}px`;
  }
}

if (!customElements.get('masonry-grid')) {
  customElements.define('masonry-grid', MasonryGrid);
}
